/** Body of `POST {baseApi}/api/auth/refresh-token`. No Authorization header is sent. */
export interface RefreshTokenPayload {
  /** The expired or expiring access token. */
  token: string;
  /** The refresh token issued alongside it at login. */
  refreshToken: string;
}

/**
 * The `data` block of a refresh response. Exact field names are not contractually
 * fixed, so every field is optional and read through {@link extractTokenPair}.
 */
export interface RefreshTokenData {
  token?: string;
  accessToken?: string;
  access_token?: string;
  jwtToken?: string;
  refreshToken?: string;
  refresh_token?: string;
  tokenType?: string;
  token_type?: string;
  [key: string]: unknown;
}

export interface RefreshTokenResponse {
  success?: boolean;
  statusCode?: string | number;
  message?: string;
  data?: RefreshTokenData | null;
}

export interface TokenPair {
  token: string;
  refreshToken: string;
  tokenType?: string;
}

/** Thrown when a refresh cannot produce a usable access token. */
export class SessionRefreshError extends Error {
  constructor(
    message: string,
    readonly reason?: unknown,
  ) {
    super(message);
    this.name = 'SessionRefreshError';
  }
}

function pick(
  source: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

/**
 * Pulls the new token pair out of a refresh response.
 *
 * Deliberately tolerant: the backend may nest the pair under `data` or return it
 * flat, and may name the access token `token` or `accessToken` (camel or snake
 * case). Returns `null` for any failure shape — including `success: false` on an
 * HTTP 200 — so that both failure modes funnel into one error path.
 *
 * Note this only validates *shape*. Callers must additionally check that the
 * returned token is not already expired, in case the backend echoes the old one.
 */
export function extractTokenPair(
  res: RefreshTokenResponse | null | undefined,
): TokenPair | null {
  if (!res || typeof res !== 'object') return null;
  if (res.success === false) return null;

  const data = (res.data ?? res) as Record<string, unknown>;
  if (!data || typeof data !== 'object') return null;

  const token = pick(data, [
    'token',
    'accessToken',
    'access_token',
    'jwtToken',
  ]);
  if (!token) return null;

  return {
    token,
    refreshToken: pick(data, ['refreshToken', 'refresh_token']) ?? '',
    tokenType: pick(data, ['tokenType', 'token_type']),
  };
}
